import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MetaFieldEditModal } from "../../../MetaFieldEditModal";
import type { ShopifySectionWidgetProps } from "../types";
import { useDynamicView } from "@/contexts/DynamicViewContext";
import { useResolveFieldLabel } from "@/lib/views/labels";
import { useLanguage } from "@/i18n/useLanguage";
import { getSectionTitleTranslation } from "@/lib/views/labels/translations";
import { SectionHeader } from "../../../SectionHeader";
import { useDetailDensity } from "../../../../hooks/useDetailDensity";
import { useDetailEditMode } from "../../../../hooks/useDetailEditMode";
import { useInlineFieldSave } from "@/components/inline-edit/detail/useInlineFieldSave";
import { useBufferFieldSave } from "@/components/inline-edit/detail/useBufferFieldSave";
import { useInlineFieldUndo } from "@/components/inline-edit/detail/useInlineFieldUndo";
import { getAdjacentField } from "@/components/inline-edit/detail/getAdjacentField";
import { InlineFieldRow } from "../../../InlineFieldRow";
import { itemQueryKeys } from "@/features/inventory/api/itemQueryKeys";
import type { DocFieldSchema } from "@/lib/erp/meta";
import { CARD } from "@/config/design-tokens";
import { Languages } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TranslateModal } from "../../../TranslateModal";
import { useStorefrontTenantSettings } from "@/features/storefront/settings/useStorefrontTenantSettings";

/**
 * Fallback labels for fields that may not be in metaFields.
 * item_name and item_code are sometimes missing from standard ERPNext meta.
 */
const FALLBACK_LABELS: Record<string, string> = {
  item_name: "Product Name",
  item_code: "SKU",
  item_group: "Category",
  stock_uom: "Unit",
  brand: "Brand",
  middleware_partner_key: "Partner",
  middleware_partner_key_locked: "Lock Partner Key",
  description: "Description",
};

const FIELD_DESCRIPTIONS: Record<string, string> = {
  item_name: "The display name shown to customers on your storefront and invoices",
  item_code: "Unique identifier for this product — used in barcodes, orders, and inventory tracking",
  item_group: "Product category for organizing your catalog and filtering in reports",
  stock_uom: "Default unit of measure for stock transactions (e.g. Nos, Kg, Box)",
  brand: "Manufacturer or brand name — helps customers filter and search products",
  middleware_partner_key: "Supplier/partner key used by the middleware to route price & stock updates. Changing this may affect automatic price/stock sync.",
  middleware_partner_key_locked: "When enabled, the middleware sync will not overwrite the Partner Key. Use this if you manually set a different partner.",
  description: "Detailed product description shown on your storefront product page",
};

const PRODUCT_DETAIL_FIELDS_BASE = [
  "item_name",
  "item_code",
  "item_group",
  "stock_uom",
  "brand",
] as const;

/** Fields that only appear when the item has a value for them (tenant-specific). */
const CONDITIONAL_FIELDS = ["middleware_partner_key"] as const;

/** Fields that appear when their parent conditional field is visible. */
const DEPENDENT_FIELDS: Record<string, string[]> = {
  middleware_partner_key: ["middleware_partner_key_locked"],
};

const PRODUCT_DETAIL_FIELDS_TAIL = ["description"] as const;

/**
 * ProductDetailsSection — renders core product identity fields as iPad Settings key-value rows.
 *
 * Supports dual-mode rendering:
 * - Modal mode (default): click card → MetaFieldEditModal
 * - Inline mode: click field value → edit in-place via InlineFieldRenderer
 */
export function ProductDetailsSection({
  formData,
  onFieldChange,
  disabled,
  onSave,
  onSubmit,
  isSaving,
  metaFields,
  fieldErrors,
  onClearFieldError,
}: ShopifySectionWidgetProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [translateOpen, setTranslateOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const { textSize, rowPx, rowPy, rowFirst, rowGap, dividerMx, rowLabel } = useDetailDensity();

  const { doctype, mode, style, enableSystemTranslations } = useDynamicView();
  const resolveLabel = useResolveFieldLabel();
  const { language } = useLanguage();

  // Edit mode: mobile forces modal (built into hook)
  const { editMode } = useDetailEditMode();
  const isNew = !formData?.name;

  // Inline save: network patch for existing docs, local buffer for new docs
  const inlineSave = useInlineFieldSave({
    doctype: "Item",
    docname: formData.name ?? "",
    detailKey: itemQueryKeys.detail(formData.name ?? ""),
  });
  const bufferSave = useBufferFieldSave();
  const { saveField, getFieldState, retryField, undoField, getUndoable, lastSavedField } = isNew ? bufferSave : inlineSave;

  // Undo support (Cmd+Z within 5s)
  const handleUndo = useCallback((_fieldname: string) => {
    // React Query refetch will update formData
  }, []);
  useInlineFieldUndo({ undoField, getUndoable, lastSavedField, onUndo: handleUndo });

  // Build field lookup map from metaFields for O(1) access
  const fieldsByName = useMemo(
    () => new Map(metaFields?.map((f) => [f.fieldname, f]) ?? []),
    [metaFields]
  );

  // Build dynamic field list: base fields + conditional fields (only if item has a value) + tail
  const isExistingDoc = !!formData?.name;
  const visibleFields = useMemo(() => {
    const fields: string[] = [...PRODUCT_DETAIL_FIELDS_BASE];
    for (const f of CONDITIONAL_FIELDS) {
      const val = formData?.[f];
      if (val != null && val !== "") {
        fields.push(f);
        // Include dependent fields (e.g., lock toggle after partner key)
        const deps = DEPENDENT_FIELDS[f];
        if (deps) fields.push(...deps);
      }
    }
    fields.push(...PRODUCT_DETAIL_FIELDS_TAIL);
    return fields;
  }, [formData]);

  // Resolve DocFieldSchema for each product detail field, enriching with descriptions
  const fieldSchemas = useMemo(() => {
    return visibleFields.map((fieldname) => {
      const metaField = fieldsByName.get(fieldname);
      const desc = FIELD_DESCRIPTIONS[fieldname];
      const isNamingField = fieldname === "item_code" && isExistingDoc;
      if (metaField) {
        const enriched = metaField.description ? metaField : { ...metaField, description: desc };
        return isNamingField ? { ...enriched, read_only: 1 as const } : enriched;
      }
      return {
        fieldname,
        label: FALLBACK_LABELS[fieldname] ?? fieldname,
        fieldtype: "Data",
        description: desc,
        ...(isNamingField ? { read_only: 1 as const } : {}),
      } as DocFieldSchema;
    });
  }, [fieldsByName, isExistingDoc, visibleFields]);

  const sectionTitle = useMemo(
    () => getSectionTitleTranslation("Product Details", language),
    [language]
  );

  // Plan 1928: translate affordance — shown only when the tenant has ≥2
  // enabled storefront languages and the doc exists.
  const { settings: tenantSettings } = useStorefrontTenantSettings();
  const enabledLanguageCount = useMemo(() => {
    try {
      const parsed = JSON.parse(tenantSettings?.enabled_languages || "[]");
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }, [tenantSettings?.enabled_languages]);
  const showTranslate = isExistingDoc && enabledLanguageCount >= 2;

  const translatableFields = useMemo(
    () => [
      { fieldname: "item_name", label: FALLBACK_LABELS.item_name, baseValue: String(formData.item_name ?? ""), multiline: false },
      { fieldname: "storefront_short_description", label: "Short Description", baseValue: String(formData.storefront_short_description ?? ""), multiline: true },
      { fieldname: "description", label: FALLBACK_LABELS.description, baseValue: String(formData.description ?? ""), multiline: true },
    ],
    [formData.item_name, formData.storefront_short_description, formData.description]
  );

  const currency = formData.currency || "THB";

  const handleInlineCommit = async (fieldname: string, newValue: unknown) => {
    const previousValue = formData[fieldname];
    setEditingField(null);
    onFieldChange(fieldname, newValue);
    await saveField(fieldname, newValue, previousValue);
  };

  const handleAdvance = useCallback((fieldname: string, direction: "next" | "prev") => {
    const next = getAdjacentField(fieldSchemas, fieldname, direction);
    if (next) setEditingField(next);
  }, [fieldSchemas]);

  return (
    <>
      <div>
        {/* min-h-9 keeps this header row a fixed 36px whether or not the
            optional Translate action button renders, so the card below always
            starts at the same Y as the right-column sidebar card (which uses
            the same min-h-9 header row). */}
        <div className="flex items-center justify-between min-h-9">
          {sectionTitle && <SectionHeader label={sectionTitle} textSize={textSize} />}
          {showTranslate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs text-muted-foreground"
              onClick={(e) => {
                e.stopPropagation();
                setTranslateOpen(true);
              }}
            >
              <Languages className="h-3.5 w-3.5 mr-1" />
              Translate
            </Button>
          )}
        </div>

        <div
          className={cn(
            CARD,
            editMode === "modal" && !disabled && "cursor-pointer active:scale-[0.98] transition-transform duration-100",
            disabled && "opacity-60 cursor-not-allowed"
          )}
          onClick={
            editMode === "modal"
              ? () => { if (!disabled) setModalOpen(true); }
              : undefined
          }
        >
          <div className="space-y-0">
            {fieldSchemas.map((field, index) => {
              const value = formData[field.fieldname];
              const isDescription = field.fieldname === "description";
              const resolvedLabel = resolveLabel(doctype, field, mode, style, enableSystemTranslations);
              const isFirst = index === 0;
              const isLast = index === fieldSchemas.length - 1;

              return (
                <InlineFieldRow
                  key={field.fieldname}
                  field={field}
                  value={value}
                  label={resolvedLabel.label}
                  editMode={editMode}
                  isEditing={editingField === field.fieldname}
                  onStartEdit={() => setEditingField(field.fieldname)}
                  onCommit={(v) => handleInlineCommit(field.fieldname, v)}
                  onCancel={() => setEditingField(null)}
                  isReadOnly={field.read_only === 1}
                  saveState={getFieldState(field.fieldname)}
                  currency={currency}
                  tooltip={field.description}
                  fullWidth={isDescription}
                  onAdvance={(dir) => handleAdvance(field.fieldname, dir)}
                  onRetry={() => retryField(field.fieldname)}
                  error={fieldErrors?.[field.fieldname]}
                  onClearError={() => onClearFieldError?.(field.fieldname)}
                  rowPx={rowPx}
                  rowPy={isFirst ? rowFirst : rowPy}
                  rowGap={rowGap}
                  rowLabel={rowLabel}
                  dividerMx={dividerMx}
                  isLast={isLast}
                />
              );
            })}
          </div>
        </div>
      </div>

      <MetaFieldEditModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        title={sectionTitle || "Product Details"}
        fields={fieldSchemas}
        section={undefined}
        formData={formData}
        onFieldChange={onFieldChange}
        onSubmit={onSubmit}
        onSave={onSave}
        disabled={disabled}
        isSaving={isSaving}
        documentName={formData?.name}
      />

      {showTranslate && (
        <TranslateModal
          open={translateOpen}
          onOpenChange={setTranslateOpen}
          refDoctype="Item"
          refName={formData.name}
          fields={translatableFields}
        />
      )}
    </>
  );
}
